import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ReactCompareSlider, ReactCompareSliderImage, ReactCompareSliderHandle } from "react-compare-slider";
import useEmblaCarousel from "embla-carousel-react";
import {
  Phone, Mail, MapPin, ArrowRight, Hammer, Clock, Users, Shield,
  Menu, X, Paintbrush, LayoutGrid, Star, Home
} from "lucide-react";

const PAGES = {
  "komplettservice": {
    title: "Komplettservice",
    seoTitle: "Komplettservice – Sanierung & Renovierung aus einer Hand",
    icon: Hammer,
    subtitle: "Ein Ansprechpartner für alle Gewerke",
    metaDescription: "Ein Ansprechpartner für alle Gewerke: Wände, Böden, Decken – Komplettsanierung in Bad Neuenahr-Ahrweiler. Stressfrei renovieren. Jetzt anfragen!",
  },
  "urlaubs-service": {
    title: "Urlaubs-Service",
    seoTitle: "Urlaubs-Service – Renovierung in Ihrer Abwesenheit",
    icon: Clock,
    subtitle: "Wir arbeiten, wenn Sie Urlaub machen",
    metaDescription: "Wir renovieren, während Sie verreisen: Malerarbeiten & Bodenbeläge in Bad Neuenahr-Ahrweiler. Kommen Sie zurück in ein neues Zuhause! Jetzt planen.",
  },
  "senioren-service": {
    title: "Senioren-Service",
    seoTitle: "Senioren-Service – Einfühlsame Renovierung & Beratung",
    icon: Users,
    subtitle: "Vertrauensvolle Betreuung für ältere Kunden",
    metaDescription: "Vertrauensvolle Malerarbeiten für Senioren in Bad Neuenahr-Ahrweiler. Persönliche Beratung, faire Preise & saubere Arbeit. Unverbindlich anfragen!",
  },
  "hoechste-qualitaet": {
    title: "Höchste Qualität",
    seoTitle: "Höchste Qualität – Materialien & Techniken seit 1999",
    icon: Shield,
    subtitle: "Modernste Materialien und Techniken",
    metaDescription: "Modernste Materialien und bewährte Techniken für langlebige Ergebnisse. Über 25 Jahre Erfahrung in Malerarbeiten & Bodenbelägen. Qualität, die bleibt.",
  },
  "malerhandwerk": {
    title: "Malerhandwerk",
    seoTitle: "Malerhandwerk – Innenanstriche, Fassaden & Lackierung",
    icon: Paintbrush,
    subtitle: "Innenanstriche, Fassaden, Lackierarbeiten",
    metaDescription: "Innenanstriche, Fassadengestaltung & Lackierarbeiten vom Profi in Bad Neuenahr-Ahrweiler. Sauber, termingerecht & mit Leidenschaft. Jetzt anfragen!",
    customContent: true,
  },
  "bodenhandwerk": {
    title: "Bodenhandwerk",
    seoTitle: "Bodenhandwerk vom Profi: Vinyl, Parkett & Beschichtung",
    icon: LayoutGrid,
    subtitle: "Vinyl, Kautschuk, Bodenbeschichtung, Parkett, Teppich",
    metaDescription: "Ehrliches Bodenhandwerk für Ihr Zuhause. Von Vinyl und Kautschuk bis Parkett und Teppich. Wir machen keine halben Sachen. Jetzt beraten lassen!",
    heroImages: [
      { src: "/boden-1.webp", label: "Ausgangslage: Rohbau mit Estrich" },
      { src: "/boden-2-parkett.webp", label: "In Arbeit: Fischgrät-Parkett wird verlegt" },
      { src: "/boden-3.webp", label: "Fertigstellung: Perfektes Ergebnis" },
    ],
    customContent: true,
    compareSliders: null,
  },
  "spezial-angebote": {
    title: "Spezial-Angebote",
    seoTitle: "Spezial-Angebote & Rundum-Service: Senioren, Urlaub & Sanierung",
    icon: Star,
    subtitle: "Senioren-Service, Urlaubsservice & Sanierung",
    metaDescription: "Stressfreie Sanierung mit dem Malerbetrieb Reinert. Entdecke unseren Senioren-Service, Urlaubsservice und das Komplettpaket aus einer Hand. Ehrlich & direkt.",
    customContent: true,
  },
  "referenzen": {
    title: "Referenzen",
    seoTitle: "Referenzen & Projekte: Echtes Malerhandwerk in der Praxis",
    icon: Home,
    subtitle: "Echtes Handwerk, echte Ergebnisse",
    metaDescription: "Überzeugen Sie sich selbst: Vorher-Nachher Bilder unserer Maler- und Bodenprojekte in Bad Neuenahr-Ahrweiler und Umgebung im Umkreis von 50 km.",
    headerImage: "/dach-nachher.webp",
    customContent: true,
    compareSliders: [
      { before: "/boden-1.webp", after: "/boden-3.webp", labelBefore: "Vorher: Estrich", labelAfter: "Nachher: Fischgrät-Vinyl" },
      { before: "/dach-vorher.webp", after: "/dach-nachher.webp", labelBefore: "Vorher: Rohbau", labelAfter: "Nachher: Wohntraum" },
      { before: "/wand-vorher.webp", after: "/wand-nachher.webp", labelBefore: "Vorher: Weiße Wand", labelAfter: "Nachher: Akustik Wandpaneele" },
      { before: "/flur-vorher.webp", after: "/flur-nachher.webp", labelBefore: "Vorher: Abgenutzt", labelAfter: "Nachher: Komplettsanierung" },
    ],
  },
};

const NAV_LINKS = [
  { label: "Leistungen", href: "/#leistungen" },
  { label: "Über Uns", href: "/#ueber-uns" },
  { label: "Vorteile", href: "/#vorteile" },
  { label: "Kontakt", href: "/kontakt" },
];


import { Brush, Building2, DoorOpen, Leaf, Sparkles, CalendarCheck, BadgeEuro, Layers, TreePine, Droplets, TreeDeciduous, Armchair, ScanLine, Ruler, ShieldCheck, HeartHandshake, Palmtree, Wrench, ClipboardList, HandHelping, SprayCan, MessageCircle, Palette, Camera, CheckCircle, ImageOff, SmilePlus } from "lucide-react";

function MalerhandwerkContent() {
  return (
    <div className="font-body text-reinert-grey leading-relaxed">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-reinert-grey-dark mb-6 leading-tight">
        Malerhandwerk – Innenanstriche, Fassaden & Lackierung
      </h1>
      <p className="text-lg text-reinert-grey mb-12 max-w-2xl">
        Vergessen Sie 08/15-Anstriche. Wir glauben, dass ein Raum erst durch das richtige Zusammenspiel von Material, Farbe und Präzision zu einem Zuhause wird. Beim Malerbetrieb Reinert gibt es keine halben Sachen. Wir nehmen uns Zeit für die Details, die am Ende den Unterschied machen – ehrlich, direkt und mit echtem Stolz auf unser Handwerk.
      </p>

      {/* Leistungssektion */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Sparkles className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Innenraumgestaltung</h3>
          <p className="text-sm text-reinert-grey">Von der klassischen Raufaser bis zur edlen Design-Tapete oder fugenlosen Oberflächen. Wir schaffen Atmosphäre.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Building2 className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Fassadenanstrich</h3>
          <p className="text-sm text-reinert-grey">Schutz trifft Ästhetik. Wir bewahren den Wert Ihrer Immobilie und sorgen für einen ersten Eindruck, der überzeugt.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <DoorOpen className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Lackierarbeiten</h3>
          <p className="text-sm text-reinert-grey">Ob Türen, Fenster oder Heizkörper – wir sorgen für Oberflächen, die sich sehen lassen können.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Leaf className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Ökologische Farben</h3>
          <p className="text-sm text-reinert-grey">Auf Wunsch arbeiten wir mit nachhaltigen, wohngesunden Materialien. Gut für Sie, gut für die Umwelt.</p>
        </div>
      </div>

      {/* Reinert-Versprechen */}
      <div className="rounded-2xl p-8 md:p-10 mb-16 relative overflow-hidden" style={{ background: '#0033A0' }}>
        <img src="/brush-stroke-tall-fill.webp" alt="" aria-hidden="true" draggable="false" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
        <div className="relative z-10">
        <h2 className="font-display font-bold text-2xl text-white mb-3">Das Reinert-Versprechen</h2>
        <p className="font-body text-white/80 mb-8">Was uns wichtig ist? Dass Sie wissen, woran Sie sind. Wir stehen für:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <Brush className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Sauberkeit</h3>
              <p className="text-white text-sm">Wir verlassen die Baustelle so, wie wir sie gerne vorfinden würden.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <CalendarCheck className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Termintreue</h3>
              <p className="text-white text-sm">Wenn wir sagen, wir kommen am Montag um 8 Uhr, dann stehen wir um 8 Uhr vor der Tür.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <BadgeEuro className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Preistransparenz</h3>
              <p className="text-white text-sm">Keine versteckten Kosten. Unsere Angebote sind ehrlich kalkuliert und verständlich.</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 text-center" style={{ background: '#FFF8E0' }}>
        <h3 className="font-display font-bold text-2xl mb-3" style={{ color: '#0033A0' }}>Lust auf einen Tapetenwechsel?</h3>
        <p className="font-body text-reinert-grey mb-6">Lassen Sie uns drüber sprechen.</p>
        <a href="/kontakt" className="btn-primary no-underline">
          <Phone className="w-4 h-4" />
          Jetzt Kontakt aufnehmen
        </a>
      </div>
    </div>
  );
}

function BodenhandwerkContent() {
  return (
    <div className="font-body text-reinert-grey leading-relaxed">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-reinert-grey-dark mb-6 leading-tight">
        Bodenhandwerk &amp; Bodenverlegung: Vinyl, Kautschuk, Bodenbeschichtung, Parkett &amp; Teppich
      </h1>
      <p className="text-lg text-reinert-grey mb-12 max-w-2xl">
        Ein guter Boden ist das Fundament für jedes Raumgefühl. Aber Hand aufs Herz: Ein Boden muss nicht nur gut aussehen, er muss deinen Alltag aushalten. Wir beim Malerbetrieb Reinert verlegen nicht einfach nur Material – wir schaffen Flächen, auf denen du lebst. Ohne Wenn und Aber und garantiert ohne halbe Sachen. Wir beraten dich ehrlich, welches Material zu deinem Leben passt, und verlegen es mit der Präzision, die echtes Handwerk ausmacht.
      </p>

      {/* Unsere Leistungen */}
      <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mb-8">Unsere Leistungen</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Layers className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Vinyl – Der moderne Alleskönner</h3>
          <p className="text-sm text-reinert-grey">Robust, pflegeleicht und optisch kaum noch von echtem Holz oder Stein zu unterscheiden. Ideal für alle, die Design wollen, ohne sich Sorgen um Kratzer machen zu müssen.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <TreePine className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Kautschuk – Natürliche Widerstandsfähigkeit</h3>
          <p className="text-sm text-reinert-grey">Extrem belastbar und ökologisch wertvoll. Kautschukböden sind die Spezialisten für Langlebigkeit und bieten ein unvergleichlich angenehmes Gehgefühl.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Droplets className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Bodenbeschichtung – Nahtlose Ästhetik</h3>
          <p className="text-sm text-reinert-grey">Ob für den modernen Industrie-Look im Wohnzimmer oder als belastbare Lösung in Garage und Keller. Unsere Beschichtungen sind fugenlos, hygienisch und extrem modern.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <TreeDeciduous className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Parkett – Zeitlose Qualität</h3>
          <p className="text-sm text-reinert-grey">Nichts schlägt die Wärme und Ausstrahlung von echtem Holz. Wir verlegen Parkett so, dass es Generationen überdauert – meisterhaft verarbeitet und perfekt veredelt.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300 sm:col-span-2 sm:max-w-[calc(50%-0.75rem)]" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Armchair className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Teppich – Komfort für die Sinne</h3>
          <p className="text-sm text-reinert-grey">Für maximale Gemütlichkeit und verbesserte Raumakustik. Wir zeigen dir moderne Texturen, die alles andere als altbacken wirken.</p>
        </div>
      </div>

      {/* Das Reinert-Versprechen */}
      <div className="rounded-2xl p-8 md:p-10 mb-16 relative overflow-hidden" style={{ background: '#0033A0' }}>
        <img src="/brush-stroke-tall-fill.webp" alt="" aria-hidden="true" draggable="false" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
        <div className="relative z-10">
        <h2 className="font-display font-bold text-2xl text-white mb-3">Das Reinert-Versprechen</h2>
        <p className="font-body text-white/80 mb-8">Bodenverlegung ist Vertrauenssache. Wir versprechen dir:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <ScanLine className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Untergrund-Check</h3>
              <p className="text-white text-sm">Wir fangen erst an, wenn die Basis stimmt. Ein Boden ist nur so gut wie das, was darunter liegt.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <Ruler className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Präzision bis zur Leiste</h3>
              <p className="text-white text-sm">Der Teufel steckt im Detail. Wir achten auf saubere Abschlüsse und perfekte Übergänge.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <ShieldCheck className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Ehrliche Materialwahl</h3>
              <p className="text-white text-sm">Wir verkaufen dir nichts, was nicht zu deinen Anforderungen passt. Wenn Parkett bei dir keinen Sinn macht, sagen wir dir das auch.</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 text-center" style={{ background: '#FFF8E0' }}>
        <h3 className="font-display font-bold text-2xl mb-3" style={{ color: '#0033A0' }}>Lust auf einen neuen Stand der Dinge?</h3>
        <p className="font-body text-reinert-grey mb-6">Lass uns deinen Boden planen.</p>
        <a href="/kontakt" className="btn-primary no-underline">
          <Phone className="w-4 h-4" />
          Jetzt Kontakt aufnehmen
        </a>
      </div>
    </div>
  );
}

function SpezialAngeboteContent() {
  return (
    <div className="font-body text-reinert-grey leading-relaxed">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-reinert-grey-dark mb-6 leading-tight">
        Spezial-Angebote &amp; Komplettservice: Senioren-Service, Urlaubsservice &amp; Sanierung
      </h1>
      <p className="text-lg text-reinert-grey mb-12 max-w-2xl">
        Renovierung bedeutet oft Stress – aber nicht mit uns. Wir beim Malerbetrieb Reinert wissen, dass viele Kunden davor zurückscheuen, weil sie den Aufwand fürchten: Möbel rücken, Staub wischen oder die Koordination verschiedener Handwerker.
      </p>
      <p className="text-lg text-reinert-grey mb-12 max-w-2xl -mt-6">
        Deshalb bieten wir dir Lösungen, die genau da ansetzen, wo es für dich schwierig wird. Ob du dich während deines Urlaubs entspannt zurücklehnen möchtest, Hilfe beim Möbelrücken im Alter brauchst oder eine komplette Sanierung ohne Organisations-Chaos planst: Wir machen das für dich. Ehrlich, verlässlich und garantiert ohne halbe Sachen.
      </p>

      {/* Unsere Spezial-Angebote */}
      <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mb-8">Unsere Spezial-Angebote im Überblick</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <HeartHandshake className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Senioren-Service – Wir nehmen dir die Last ab</h3>
          <p className="text-sm text-reinert-grey">Du möchtest frische Farbe, kannst aber nicht mehr schwer heben? Kein Problem. Wir räumen die Möbel aus dem Weg, nehmen Gardinen ab und hängen sie nach getaner Arbeit wieder auf. Am Ende hinterlassen wir alles blitzblank, damit du dich sofort wieder wohlfühlst.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Palmtree className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Urlaubsservice – Du entspannst, wir arbeiten</h3>
          <p className="text-sm text-reinert-grey">Stell dir vor, du kommst aus dem Urlaub wieder und dein Zuhause strahlt in neuem Glanz. Du gibst uns den Schlüssel, wir erledigen den Rest – inklusive Endreinigung. Diskretion und Vertrauen sind hier unser höchstes Gut.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Wrench className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Komplett-Sanierung &amp; Gewerke-Management</h3>
          <p className="text-sm text-reinert-grey">Du hast ein größeres Projekt? Wir ziehen verlässliche Partnerfirmen (Elektro, Sanitär, etc.) hinzu, holen Angebote ein und koordinieren den gesamten Ablauf. Du hast während der gesamten Phase nur einen Ansprechpartner: uns.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <ClipboardList className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Projektbegleitung von A bis Z</h3>
          <p className="text-sm text-reinert-grey">Wir übernehmen die gesamte Planung und Überwachung. Du sparst dir das Hinterhertelefonieren bei verschiedenen Firmen und kannst dich auf ein perfektes Ergebnis verlassen.</p>
        </div>
      </div>

      {/* Das Reinert-Versprechen */}
      <div className="rounded-2xl p-8 md:p-10 mb-16 relative overflow-hidden" style={{ background: '#0033A0' }}>
        <img src="/brush-stroke-tall-fill.webp" alt="" aria-hidden="true" draggable="false" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
        <div className="relative z-10">
        <h2 className="font-display font-bold text-2xl text-white mb-3">Das Reinert-Versprechen</h2>
        <p className="font-body text-white/80 mb-8">Dein Vertrauen ist unser Fundament. Unser Spezial-Service bedeutet:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <HandHelping className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Maximale Entlastung</h3>
              <p className="text-white text-sm">Wir kümmern uns um die Details, damit du es nicht musst.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <SprayCan className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Absolute Sauberkeit</h3>
              <p className="text-white text-sm">Wir verlassen dein Zuhause so, dass du direkt wieder einziehen kannst.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <MessageCircle className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Ehrliche Kommunikation</h3>
              <p className="text-white text-sm">Wir besprechen jeden Schritt vorab, damit es keine Überraschungen gibt.</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 text-center" style={{ background: '#FFF8E0' }}>
        <h3 className="font-display font-bold text-2xl mb-3" style={{ color: '#0033A0' }}>Sanierung ohne Kopfschmerzen?</h3>
        <p className="font-body text-reinert-grey mb-6">Wir koordinieren, du genießt. Hol dir jetzt dein ehrliches Rundum-sorglos-Angebot.</p>
        <a href="/kontakt" className="btn-primary no-underline">
          <Phone className="w-4 h-4" />
          Jetzt Kontakt aufnehmen
        </a>
      </div>
    </div>
  );
}

function ReferenzenContent() {
  return (
    <div className="font-body text-reinert-grey leading-relaxed">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-reinert-grey-dark mb-6 leading-tight">
        Referenzen &amp; Projekte: Echtes Malerhandwerk und Bodenverlegung in der Praxis
      </h1>
      <p className="text-lg text-reinert-grey mb-12 max-w-2xl">
        Bilder sagen mehr als tausend Worte – aber sie erzählen meistens nicht die ganze Geschichte. Was du hier siehst, ist das Ergebnis von präziser Vorbereitung, ehrlicher Materialwahl und dem Anspruch, jedes Detail perfekt umzusetzen. Wir zeigen dir hier keine geschönten Katalogbilder, sondern echte Projekte, die wir für unsere Kunden im Rheinland und darüber hinaus realisiert haben. Überzeug dich selbst davon, was passiert, wenn man keine halben Sachen macht.
      </p>

      {/* Unsere Projekt-Bereiche */}
      <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mb-8">Unsere Projekt-Bereiche</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Palette className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Innenraumgestaltung</h3>
          <p className="text-sm text-reinert-grey">Von modernen Farbakzenten bis hin zu hochwertigen Wandtechniken. Wir zeigen dir, wie Räume durch Farbe an Charakter gewinnen.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Building2 className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Fassaden &amp; Außenansichten</h3>
          <p className="text-sm text-reinert-grey">Schutz trifft Ästhetik. Unsere Referenzen im Außenbereich zeigen, wie wir Werte erhalten und Häusern ein neues Gesicht geben.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Layers className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Bodenhandwerk in Aktion</h3>
          <p className="text-sm text-reinert-grey">Vinyl, Parkett oder fugenlose Beschichtung – sieh dir an, wie unsere Böden in echtem Licht und echten Räumen wirken.</p>
        </div>
        <div className="p-6 rounded-2xl border border-gray-100 hover:border-reinert-yellow-DEFAULT transition-colors duration-300" style={{ background: '#F4F7FC' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#0033A0' }}>
            <Camera className="w-6 h-6" style={{ color: '#FFCC00' }} />
          </div>
          <h3 className="font-display font-bold text-lg text-reinert-grey-dark mb-2">Spezial-Projekte</h3>
          <p className="text-sm text-reinert-grey">Vorher-Nachher-Einblicke in komplette Sanierungen, bei denen wir die gesamte Koordination übernommen haben.</p>
        </div>
      </div>
    </div>
  );
}

function ReferenzenVersprechen() {
  return (
    <div className="font-body text-reinert-grey leading-relaxed mt-16">
      {/* Das Reinert-Versprechen */}
      <div className="rounded-2xl p-8 md:p-10 mb-16 relative overflow-hidden" style={{ background: '#0033A0' }}>
        <img src="/brush-stroke-tall-fill.webp" alt="" aria-hidden="true" draggable="false" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
        <div className="relative z-10">
        <h2 className="font-display font-bold text-2xl text-white mb-3">Das Reinert-Versprechen</h2>
        <p className="font-body text-white/80 mb-8">Transparenz ist uns wichtig. Deshalb gilt für unsere Referenzen:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <CheckCircle className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Echte Baustellen</h3>
              <p className="text-white text-sm">Alles, was du hier siehst, wurde von unserem Team handwerklich umgesetzt.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <ImageOff className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Keine Filter</h3>
              <p className="text-white text-sm">Wir lassen die Qualität der Arbeit für sich sprechen. Ein sauberer Abschluss braucht keine Nachbearbeitung.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,204,0,0.2)' }}>
              <SmilePlus className="w-5 h-5" style={{ color: '#FFCC00' }} />
            </div>
            <div>
              <h3 className="font-display font-bold text-white text-base mb-1">Zufriedene Kunden</h3>
              <p className="text-white text-sm">Hinter jedem Bild steht ein Kunde, der heute auf einem Reinert-Boden steht oder auf Reinert-Wände schaut.</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 text-center" style={{ background: '#FFF8E0' }}>
        <h3 className="font-display font-bold text-2xl mb-3" style={{ color: '#0033A0' }}>Dein Projekt sieht aktuell noch ganz anders aus?</h3>
        <p className="font-body text-reinert-grey mb-6">Wir ändern das – ehrlich, sauber und ohne Stress. Schreib uns!</p>
        <a href="/kontakt" className="btn-primary no-underline">
          <Phone className="w-4 h-4" />
          Jetzt Kontakt aufnehmen
        </a>
      </div>
    </div>
  );
}

function CustomContent({ slug }) {
  if (slug === "malerhandwerk") return <MalerhandwerkContent />;
  if (slug === "bodenhandwerk") return <BodenhandwerkContent />;
  if (slug === "spezial-angebote") return <SpezialAngeboteContent />;
  if (slug === "referenzen") return <ReferenzenContent />;
  return null;
}


export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = PAGES[slug];
  const [menuOpen, setMenuOpen] = useState(false);

function HeroImageSlider({ images, title, subtitle, icon: Icon }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    const interval = setInterval(() => emblaApi.scrollNext(), 5000);
    return () => { clearInterval(interval); emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative pt-20" data-testid="hero-image-slider">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex" style={{ backfaceVisibility: 'hidden' }}>
          {images.map((img, i) => (
            <div className="flex-[0_0_100%] min-w-0 relative" key={i}>
              <img src={img.src} alt={img.label} className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="max-w-4xl mx-auto">
                  {i === 0 && (
                    <>
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,204,0,0.2)', border: '2px solid rgba(255,204,0,0.4)' }}>
                        <Icon className="w-7 h-7" style={{ color: '#FFCC00' }} />
                      </div>
                      <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white mb-2">{title}</h1>
                      <p className="font-body text-white/80 text-lg mb-3">{subtitle}</p>
                    </>
                  )}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,51,160,0.8)', backdropFilter: 'blur(8px)' }}>
                    <span className="font-body font-semibold text-sm text-white">Schritt {i + 1} von {images.length}</span>
                    <span className="text-white/60 mx-1">|</span>
                    <span className="font-body text-sm text-white/80">{img.label}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-2 absolute bottom-4 right-8 z-10">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => emblaApi?.scrollTo(i)}
            className="border-none cursor-pointer rounded-full transition-all duration-300"
            style={{
              width: selectedIndex === i ? 24 : 8,
              height: 8,
              background: selectedIndex === i ? '#FFCC00' : 'rgba(255,255,255,0.4)',
            }}
            data-testid={`hero-slider-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (page) {
      document.title = page.seoTitle || page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && page.metaDescription) metaDesc.setAttribute('content', page.metaDescription);
    }
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center font-body">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold mb-4">Seite nicht gefunden</h1>
          <button onClick={() => navigate("/")} className="btn-primary">Zurück zur Startseite</button>
        </div>
      </div>
    );
  }

  const Icon = page.icon;

  return (
    <div className="min-h-screen bg-white" data-testid={`page-${slug}`}>
      {/* Same Navigation as main page */}
      <nav className={`nav-bar fixed top-0 left-0 right-0 z-50 bg-white ${scrolled ? "scrolled" : ""}`} data-testid="service-navigation">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 no-underline" data-testid="logo-button">
            <img src="/logo-reinert.webp" alt="Malerbetrieb Reinert – Malerarbeiten und Bodenbeläge in Bad Neuenahr-Ahrweiler" style={{ height: 72, width: 'auto' }} />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <Link key={link.href} to={link.href} className="nav-link bg-transparent border-none font-body no-underline">
                {link.label}
              </Link>
            ))}
            <Link to="/kontakt" className="btn-primary no-underline">
              <Phone className="w-4 h-4" />
              Beratung anfragen
            </Link>
          </div>
          <button className="lg:hidden bg-transparent border-none cursor-pointer p-2 text-reinert-grey-dark" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-toggle">
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        <div className="flex justify-end p-6">
          <button onClick={() => setMenuOpen(false)} className="bg-transparent border-none cursor-pointer text-white"><X className="w-8 h-8" /></button>
        </div>
        <div className="flex flex-col items-start px-10 gap-6 mt-8">
          {NAV_LINKS.map((link) => (
            <Link key={link.href} to={link.href} onClick={() => setMenuOpen(false)} className="mobile-menu-link bg-transparent border-none no-underline">
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Hero Banner */}
      {page.heroImages ? (
        <HeroImageSlider images={page.heroImages} title={page.title} subtitle={page.subtitle} icon={Icon} />
      ) : page.headerImage ? (
        <div className="relative overflow-hidden pt-20">
          <img src={page.headerImage} alt={`${page.title} – Referenzprojekt Malerbetrieb Reinert`} className="w-full h-[300px] md:h-[400px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,204,0,0.2)', border: '2px solid rgba(255,204,0,0.4)' }}>
                <Icon className="w-7 h-7" style={{ color: '#FFCC00' }} />
              </div>
              <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white mb-2">{page.title}</h1>
              <p className="font-body text-white/80 text-lg">{page.subtitle}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative overflow-hidden pt-32 pb-20 md:pt-36 md:pb-28 px-6" style={{ background: '#0033A0' }}>
          <div className="absolute inset-0" style={{ backgroundColor: '#0033A0' }} />
          <img
            src="/brush-slider-bg.webp"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: 0.7, mixBlendMode: 'overlay' }}
          />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'rgba(255, 204, 0, 0.25)', border: '1px solid rgba(255, 204, 0, 0.35)' }}>
              <Icon className="w-8 h-8" style={{ color: '#FFCC00' }} />
            </div>
            <h1 className="font-display font-extrabold text-5xl md:text-6xl text-white mb-4">{page.title}</h1>
            <p className="font-body text-white/80 text-xl">{page.subtitle}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        {page.customContent ? (
          <CustomContent slug={slug} />
        ) : (
        <div className="font-body text-reinert-grey leading-relaxed">
          <p className="text-xl text-reinert-grey-dark font-medium mb-8">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
          </p>
          <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mt-12 mb-4">Unsere Leistungen im Detail</h2>
          <p className="mb-4">
            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.
          </p>
          <p className="mb-4">
            Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit.
          </p>
          <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mt-12 mb-4">Warum Sie sich für uns entscheiden sollten</h2>
          <p className="mb-6">
            At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.
          </p>
          <ul className="space-y-3 my-8">
            {["Similique sunt in culpa qui officia deserunt mollitia animi", "Id est laborum et dolorum fuga et harum quidem rerum facilis", "Nam libero tempore cum soluta nobis est eligendi optio"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-reinert-yellow-DEFAULT flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-reinert-blue-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p>
            Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae.
          </p>
        </div>
        )}

        {/* Before/After Compare Slider */}
        {page.compareSliders && (
          <div className="mt-16">
            <h2 className="font-display font-bold text-2xl text-reinert-grey-dark mb-6">Vorher / Nachher</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {page.compareSliders.map((slider, i) => (
                <div key={i} className="rounded-2xl overflow-hidden" style={{ border: '3px solid #FFCC00' }}>
                  <ReactCompareSlider
                    itemOne={<ReactCompareSliderImage src={slider.before} alt={slider.labelBefore} />}
                    itemTwo={<ReactCompareSliderImage src={slider.after} alt={slider.labelAfter} />}
                    handle={
                      <ReactCompareSliderHandle
                        buttonStyle={{
                          backgroundColor: '#FFCC00',
                          border: '3px solid white',
                          boxShadow: '0 0 12px rgba(0,0,0,0.3)',
                          width: 40,
                          height: 40,
                        }}
                        linesStyle={{ width: 3, color: '#FFCC00' }}
                      />
                    }
                    style={{ height: 'auto', aspectRatio: '16/9' }}
                    position={50}
                  />
                  <div className="flex justify-between px-4 py-3" style={{ background: '#0033A0' }}>
                    <span className="font-body font-semibold text-sm text-white">{slider.labelBefore}</span>
                    <span className="font-body font-semibold text-sm" style={{ color: '#FFCC00' }}>{slider.labelAfter}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referenzen: Versprechen + CTA after sliders */}
        {slug === "referenzen" && <ReferenzenVersprechen />}

        {/* CTA - only for pages without custom content */}
        {!page.customContent && (
        <div className="mt-16 p-8 rounded-2xl text-center" style={{ background: '#FFF8E0' }}>
          <h3 className="font-display font-bold text-2xl mb-3" style={{ color: '#0033A0' }}>Interesse geweckt?</h3>
          <p className="font-body text-reinert-grey mb-6">Kontaktieren Sie uns für eine unverbindliche Beratung.</p>
          <Link to="/kontakt" className="btn-primary no-underline" data-testid="service-cta">
            <Phone className="w-4 h-4" />
            Beratung anfragen
          </Link>
        </div>
        )}
      </div>

      {/* Full Footer */}
      <footer className="bg-reinert-grey-dark pt-16 pb-8 px-6 lg:px-8" data-testid="footer-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-5">
                <img src="/logo-reinert.webp" alt="Malerbetrieb Reinert – Malerarbeiten und Bodenbeläge seit 1999" style={{ height: 44, width: 'auto' }} />
              </div>
              <p className="text-white/50 font-body text-sm leading-relaxed">
                Qualitätshandwerk seit 1999 in Bad Neuenahr-Ahrweiler. Ihr zuverlässiger Partner für Maler- und Bodenarbeiten.
              </p>
            </div>
            <div>
              <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">LEISTUNGEN</h4>
              <div className="flex flex-col gap-3">
                <a href="/service/malerhandwerk" className="footer-link">Malerhandwerk</a>
                <a href="/service/bodenhandwerk" className="footer-link">Bodenhandwerk</a>
                <a href="/service/spezial-angebote" className="footer-link">Spezial-Angebote</a>
                <a href="/service/referenzen" className="footer-link">Referenzen</a>
              </div>
            </div>
            <div>
              <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">UNTERNEHMEN</h4>
              <div className="flex flex-col gap-3">
                <a href="/impressum" className="footer-link">Impressum</a>
                <a href="/datenschutz" className="footer-link">Datenschutz</a>
                <a href="/kontakt" className="footer-link">Kontakt</a>
              </div>
            </div>
            <div>
              <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">KONTAKT</h4>
              <div className="flex flex-col gap-4">
                <a href="tel:+492641205474" className="flex items-center gap-3 footer-link">
                  <Phone className="w-4 h-4 flex-shrink-0" style={{ color: '#FFCC00' }} /> 02641 / 205474
                </a>
                <a href="mailto:info@malerbetrieb-reinert.de" className="flex items-center gap-3 footer-link">
                  <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#FFCC00' }} /> info@malerbetrieb-reinert.de
                </a>
                <div className="flex items-start gap-3 footer-link">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#FFCC00' }} />
                  <span>Walporzheimer Str. 100<br />53474 Bad Neuenahr-Ahrweiler</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 font-body text-xs">&copy; {new Date().getFullYear()} Malerbetrieb Reinert. Alle Rechte vorbehalten.</p>
            <div className="flex items-center gap-1 text-white/30 text-xs font-body">
              <span>Handwerk mit</span>
              <svg className="w-3.5 h-3.5" style={{ color: '#FFCC00' }} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              <span>aus Bad Neuenahr</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
